import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

export default function HeroSection() {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Animated Background Pattern - Desktop only */}
      <div className="absolute inset-0 opacity-10">
        <motion.div 
          className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(16, 185, 129, 0.3) 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}
          animate={window.innerWidth >= 768 ? {
            backgroundPosition: ['0px 0px', '48px 48px'],
          } : {}}
          transition={window.innerWidth >= 768 ? { duration: 20, repeat: Infinity, ease: "linear" } : {}}
        ></motion.div>
      </div>

      {/* Animated gradient orbs - Desktop only */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <motion.div
          animate={window.innerWidth >= 768 ? {
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.5, 0.2],
          } : {}}
          transition={window.innerWidth >= 768 ? { duration: 10, repeat: Infinity, ease: "easeInOut" } : {}}
          className="absolute -top-20 sm:-top-32 lg:-top-40 -left-20 sm:-left-32 lg:-left-40 w-64 sm:w-80 lg:w-96 h-64 sm:h-80 lg:h-96 bg-emerald-500/30 rounded-full blur-3xl"
          style={{ opacity: window.innerWidth >= 768 ? undefined : 0.3 }}
        ></motion.div>
        <motion.div
          animate={window.innerWidth >= 768 ? {
            scale: [1, 1.3, 1],
            opacity: [0.15, 0.35, 0.15],
          } : {}}
          transition={window.innerWidth >= 768 ? { duration: 12, repeat: Infinity, ease: "easeInOut", delay: 2 } : {}}
          className="absolute -bottom-20 sm:-bottom-32 lg:-bottom-40 -right-20 sm:-right-32 lg:-right-40 w-72 sm:w-96 lg:w-[500px] h-72 sm:h-96 lg:h-[500px] bg-teal-500/25 rounded-full blur-3xl"
          style={{ opacity: window.innerWidth >= 768 ? undefined : 0.2 }}
        ></motion.div>
        <motion.div
          animate={window.innerWidth >= 768 ? {
            scale: [1, 1.4, 1],
            opacity: [0.15, 0.4, 0.15],
            rotate: [0, 180, 360],
          } : {}}
          transition={window.innerWidth >= 768 ? { duration: 15, repeat: Infinity, ease: "easeInOut" } : {}}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-64 sm:w-96 lg:w-[600px] h-64 sm:h-96 lg:h-[600px] bg-cyan-500/20 rounded-full blur-3xl"
          style={{ opacity: window.innerWidth >= 768 ? undefined : 0.25 }}
        ></motion.div>
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-20 sm:py-24 lg:py-32">
        <div className="text-center">
          {/* Premium Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2, type: "spring", stiffness: 200 }}
            className="inline-flex items-center gap-1.5 sm:gap-2 bg-gradient-to-r from-emerald-500/20 to-teal-500/20 backdrop-blur-sm border border-emerald-400/30 rounded-full px-3 sm:px-4 lg:px-6 py-1.5 sm:py-2 lg:py-3 mb-4 sm:mb-6 lg:mb-8"
          >
            <motion.span 
              className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-emerald-400 rounded-full"
              animate={window.innerWidth >= 768 ? { scale: [1, 1.3, 1], opacity: [1, 0.5, 1] } : {}}
              transition={window.innerWidth >= 768 ? { duration: 2, repeat: Infinity } : {}}
            ></motion.span>
            <span className="text-xs sm:text-sm font-bold text-emerald-400 tracking-wide uppercase">Offre Exceptionnelle</span>
            <motion.span 
              className="text-xs sm:text-sm font-semibold text-white"
              animate={window.innerWidth >= 768 ? { scale: [1, 1.05, 1] } : {}}
              transition={window.innerWidth >= 768 ? { duration: 2, repeat: Infinity } : {}}
            >Installation dès 1€</motion.span>
          </motion.div>

          {/* Main Title */}
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white mb-3 sm:mb-4 lg:mb-6 leading-tight"
          >
            <motion.span
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              Rénovation LED
            </motion.span>
            <br />
            <motion.span 
              className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 bg-clip-text text-transparent"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              Industrielle & Tertiaire
            </motion.span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300 mb-6 sm:mb-8 lg:mb-12 leading-relaxed max-w-3xl mx-auto px-4"
          >
            Transformez votre éclairage industriel avec nos solutions LED certifiées.
            <br />
            <motion.strong 
              className="text-white"
              animate={window.innerWidth >= 768 ? { opacity: [1, 0.8, 1] } : {}}
              transition={window.innerWidth >= 768 ? { duration: 2, repeat: Infinity } : {}}
            >Économisez jusqu'à 75% d'énergie</motion.strong> grâce au financement CEE BAT-EQ-127
          </motion.p>

          {/* Key Stats - Figé sur mobile */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 lg:gap-6 mb-6 sm:mb-8 lg:mb-12 max-w-4xl mx-auto"
          >
            {[
              { value: '1€', label: 'Installation', icon: 'ri-money-euro-circle-line', delay: 0 },
              { value: '75%', label: 'Économies', icon: 'ri-leaf-line', delay: 0.1 },
              { value: '24h', label: 'Étude gratuite', icon: 'ri-time-line', delay: 0.2 },
              { value: '5 ans', label: 'Garantie', icon: 'ri-shield-check-line', delay: 0.3 }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.8 + stat.delay }}
                whileHover={window.innerWidth >= 768 ? { 
                  scale: 1.05,
                  y: -5,
                  transition: { duration: 0.2 }
                } : {}}
                className={`relative group text-center ${
                  window.innerWidth >= 768 ? 'hover:z-10' : ''
                } cursor-pointer`}
                style={window.innerWidth < 768 ? { transform: 'none' } : {}}
              >
                {/* Decorative Corner - Desktop uniquement */}
                {window.innerWidth >= 768 && (
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-300 animate-pulse"></div>
                )}
                
                <div className={`relative bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg sm:rounded-xl lg:rounded-2xl p-3 sm:p-4 lg:p-6 transition-all duration-300 ${
                  window.innerWidth >= 768 ? 'group-hover:bg-white/20 group-hover:border-white/40 group-hover:shadow-lg' : ''
                }`}>
                  {/* Animated Background - Desktop uniquement */}
                  {window.innerWidth >= 768 && (
                    <motion.div 
                      className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-lg sm:rounded-xl lg:rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                      whileHover={{ scale: 1.02 }}
                    ></motion.div>
                  )}
                  
                  <div className="relative z-10">
                    <motion.div 
                      className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 mx-auto mb-2 sm:mb-3 lg:mb-4 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-full flex items-center justify-center shadow-md"
                      whileHover={window.innerWidth >= 768 ? { 
                        scale: 1.1, 
                        rotate: [0, -5, 5, 0],
                        transition: { duration: 0.3 }
                      } : {}}
                      style={window.innerWidth < 768 ? { transform: 'none' } : {}}
                    >
                      <i className={`${stat.icon} text-white text-sm sm:text-base lg:text-lg`}></i>
                    </motion.div>
                    
                    <motion.div 
                      className="text-xl sm:text-2xl lg:text-3xl font-black text-white mb-1 sm:mb-2"
                      whileHover={window.innerWidth >= 768 ? { 
                        scale: 1.1,
                        color: "#fbbf24",
                        transition: { duration: 0.2 }
                      } : {}}
                      style={window.innerWidth < 768 ? { transform: 'none', color: 'white' } : {}}
                    >
                      {stat.value}
                    </motion.div>
                    
                    <div className="text-xs sm:text-sm lg:text-base text-emerald-100 font-medium">
                      {stat.label}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="flex flex-col sm:flex-row gap-8 sm:gap-4 lg:gap-6 justify-center items-center mb-8 sm:mb-16"
          >
            <motion.div
              whileHover={window.innerWidth >= 768 ? { scale: 1.05 } : {}}
              whileTap={window.innerWidth >= 768 ? { scale: 0.95 } : {}}
              className="w-full sm:w-auto"
            >
              <Link
                to="/etude-gratuite"
                className="group bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-6 sm:px-8 lg:px-10 py-3 sm:py-4 lg:py-5 rounded-xl sm:rounded-2xl text-sm sm:text-base lg:text-lg font-bold hover:from-emerald-600 hover:to-teal-600 shadow-lg shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all cursor-pointer whitespace-nowrap w-full sm:w-auto text-center"
              >
                <i className="ri-flashlight-line mr-2 text-lg sm:text-xl lg:text-2xl group-hover:rotate-12 transition-transform"></i>
                Demander une étude gratuite
              </Link>
            </motion.div>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.2 }}
            className="mt-6 sm:mt-8 lg:mt-12 flex flex-wrap items-center justify-center gap-4 sm:gap-6 lg:gap-8 text-gray-400 text-xs sm:text-sm px-4"
          >
            {[
              'Certifié NF C 15-100',
              'Certifié CEE BAT-EQ-127',
              'Entreprise RGE'
            ].map((cert, index) => (
              <motion.div 
                key={index}
                className="flex items-center gap-1.5 sm:gap-2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 1.3 + index * 0.1 }}
                whileHover={window.innerWidth >= 768 ? { scale: 1.1 } : {}}
              >
                <motion.i 
                  className="ri-checkbox-circle-fill text-emerald-400 text-sm sm:text-base lg:text-lg"
                  animate={window.innerWidth >= 768 ? { scale: [1, 1.2, 1] } : {}}
                  transition={window.innerWidth >= 768 ? { duration: 2, repeat: Infinity, delay: index * 0.3 } : {}}
                ></motion.i>
                <span>{cert}</span>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.5 }}
        className="absolute bottom-4 sm:bottom-6 lg:bottom-8 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={window.innerWidth >= 768 ? { y: [0, 10, 0] } : {}}
          transition={window.innerWidth >= 768 ? { duration: 2, repeat: Infinity, ease: "easeInOut" } : {}}
          className="flex flex-col items-center gap-1 sm:gap-2 cursor-pointer"
        >
          <span className="text-xs text-gray-400 font-semibold tracking-wider uppercase">Découvrir</span>
          <motion.i 
            className="ri-arrow-down-line text-xl sm:text-2xl text-emerald-400"
            animate={window.innerWidth >= 768 ? { y: [0, 5, 0] } : {}}
            transition={window.innerWidth >= 768 ? { duration: 1.5, repeat: Infinity } : {}}
          ></motion.i>
        </motion.div>
      </motion.div>
    </section>
  );
}
